import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { ILMSAlert } from '../../components/ILMSAlert';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSFormField, ILMSInput, ILMSSelect } from '../../components/ILMSForm';
import { getApiErrorMessage } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { english, listForms, type FormEntry } from '../../features/common/formRegistry';
import {
  canHaveDefault, fieldLock, formSettingsApi, useFormSettings,
  type FieldLock, type FieldRule, type FormRule, type FormSettings, type FormValue,
} from '../../features/common/formSettings';
import { useReferenceData, type RefKey } from '../../features/common/referenceData';
import type { FieldConfig } from '../../features/common/types';
import { cn } from '../../utils/cn';
import { SaveBar } from './appearance/SaveBar';

const LOCK_TEXT: Record<Exclude<FieldLock, 'free'>, string> = {
  always: 'Always required',
  depends: 'Depends on other choices',
  fixed: 'Filled in by the app',
};

const isSet = (r?: FieldRule) => !!r && (r.required || r.hidden || !!r.label || !!r.help || (r.default !== undefined && r.default !== null && r.default !== ''));
const ruleChanged = (rule?: FormRule) => !!rule && (Object.values(rule.fields ?? {}).some(isSet) || Object.keys(rule.options ?? {}).length > 0);

/** Administration -> Entry Form Settings: pick a form from the dropdown, then
 * per field make it required, hide it, rename it, change its help text or
 * give it a starting value - for every web entry form, no developer needed. */
export function EntryFormsTab() {
  const { can } = useAuth();
  const canEdit = can('System Settings', 'Edit');
  const queryClient = useQueryClient();
  const settingsQuery = useFormSettings();
  const forms = useMemo(() => listForms(), []);
  const [formId, setFormId] = useState(forms[0]?.id ?? '');
  const [draft, setDraft] = useState<FormSettings>({ forms: {} });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settingsQuery.data) setDraft(settingsQuery.data);
  }, [settingsQuery.data]);

  const save = useMutation({
    mutationFn: () => formSettingsApi.save(draft),
    onSuccess: (data) => {
      queryClient.setQueryData(['form-settings'], data);
      setDraft(data);
      setSaved(true);
    },
  });

  const dirty = JSON.stringify(draft) !== JSON.stringify(settingsQuery.data ?? { forms: {} });
  const form = forms.find((f) => f.id === formId);
  const rule: FormRule = draft.forms[formId] ?? {};
  const pages = [...new Set(forms.map((f) => f.page))];

  const setRule = (next: FormRule) => {
    setSaved(false);
    setDraft((d) => ({ forms: { ...d.forms, [formId]: next } }));
  };
  const setField = (name: string, patch: Partial<FieldRule>) => {
    const fields = { ...(rule.fields ?? {}) };
    fields[name] = { ...(fields[name] ?? {}), ...patch };
    if (!isSet(fields[name])) delete fields[name];
    setRule({ ...rule, fields });
  };
  const setOption = (key: string, value: FormValue | undefined) => {
    const options = { ...(rule.options ?? {}) };
    if (value === undefined) delete options[key];
    else options[key] = value;
    setRule({ ...rule, options });
  };

  if (settingsQuery.isLoading) return <p className="text-secondary text-ink-500">Loading form settings…</p>;

  return (
    <div className="flex flex-col gap-4">
      {settingsQuery.isError && <ILMSAlert tone="critical">{getApiErrorMessage(settingsQuery.error, 'Unable to load the form settings.')}</ILMSAlert>}
      <ILMSCard
        title="Entry Form Settings"
        subtitle="Choose a form, then decide for each field whether it must be filled in, is hidden, what it is called, its help text and the value it starts with. Changes apply to the web app as soon as they are saved."
      >
        <div className="flex flex-col gap-4">
          <ILMSFormField label="Entry form" htmlFor="entry_form" helperText={`${forms.length} forms · ${Object.entries(draft.forms).filter(([, r]) => ruleChanged(r)).length} changed (marked ●)`}>
            <ILMSSelect id="entry_form" value={formId} onChange={(e) => setFormId(e.target.value)} className="max-w-xl">
              {pages.map((page) => (
                <optgroup key={page} label={page}>
                  {forms.filter((f) => f.page === page).map((f) => (
                    <option key={f.id} value={f.id}>{f.title}{ruleChanged(draft.forms[f.id]) ? '  ●' : ''}</option>
                  ))}
                </optgroup>
              ))}
            </ILMSSelect>
          </ILMSFormField>

          {form && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-card-title">{form.page} → {form.title}</p>
                {ruleChanged(rule) && canEdit && (
                  <button type="button" className="inline-flex items-center gap-1 text-helper-text text-critical-600 hover:underline" onClick={() => setRule({})}>
                    <RotateCcw className="h-3.5 w-3.5" /> Put this form back as it was
                  </button>
                )}
              </div>
              {form.limited && (
                <ILMSAlert tone="info">This form is laid out by hand: you can make its optional fields required and set its options. Renaming, hiding and starting values aren’t available here.</ILMSAlert>
              )}

              {(form.options ?? []).map((o) => (
                <ILMSFormField key={o.key} label={o.label} htmlFor={`opt-${o.key}`} helperText={`${o.help ?? ''} Standard: ${o.defaultValue}.`}>
                  <ILMSInput
                    id={`opt-${o.key}`} type="number" min={o.min} max={o.max} disabled={!canEdit} className="max-w-[10rem]"
                    value={rule.options?.[o.key] !== undefined ? String(rule.options[o.key]) : String(o.defaultValue)}
                    onChange={(e) => {
                      const n = Math.min(o.max, Math.max(o.min, Number(e.target.value) || 0));
                      setOption(o.key, n === o.defaultValue ? undefined : n);
                    }}
                  />
                </ILMSFormField>
              ))}

              <div className="hidden lg:grid grid-cols-[1.3fr_1fr_0.5fr_1.2fr_1.5fr_1.2fr_auto] gap-3 border-b border-ink-300/20 pb-1 text-label text-ink-500">
                <span>Field</span><span>Must fill in?</span><span>Hide</span><span>Name shown</span><span>Help text</span><span>Starting value</span><span />
              </div>
              <div className="flex flex-col divide-y divide-ink-300/10">
                {form.fields.map((f) => (
                  <FieldRow key={f.name} form={form} field={f} rule={rule.fields?.[f.name]} canEdit={canEdit} onChange={(patch) => setField(f.name, patch)}
                    onReset={() => setField(f.name, { required: null, hidden: false, label: null, help: null, default: null })} />
                ))}
              </div>
            </>
          )}
        </div>
      </ILMSCard>
      {save.isError && <ILMSAlert tone="critical">{getApiErrorMessage(save.error, 'Unable to save the form settings.')}</ILMSAlert>}
      <SaveBar canEdit={canEdit} dirty={dirty} saving={save.isPending} saved={saved} onSave={() => save.mutate()}
        onReset={dirty ? () => setDraft(settingsQuery.data ?? { forms: {} }) : undefined} resetLabel="Undo unsaved changes" />
      <p className="text-helper-text text-ink-500">
        Fields the system itself needs stay required. These settings are for the web app; the mobile app keeps its own forms.
      </p>
    </div>
  );
}

function FieldRow({ form, field, rule, canEdit, onChange, onReset }: {
  form: FormEntry; field: FieldConfig<any>; rule?: FieldRule; canEdit: boolean;
  onChange: (patch: Partial<FieldRule>) => void; onReset: () => void;
}) {
  const lock = fieldLock(field);
  const free = lock === 'free';
  const refKey = typeof field.optionsKey === 'string' ? (field.optionsKey as RefKey) : undefined;
  const { data: ref } = useReferenceData(!form.limited && refKey ? [refKey] : []);
  const original = english(field.label);
  const disabled = !canEdit;
  const cell = 'flex flex-col gap-1 min-w-0';
  const mobileLabel = 'lg:hidden text-helper-text text-ink-400';

  const defaultControl = () => {
    if (form.limited || !canHaveDefault(field)) return <span className="text-helper-text text-ink-300">—</span>;
    const value = rule?.default ?? '';
    const setDefault = (v: FormValue | null) => onChange({ default: v });
    if (field.type === 'select') {
      const options = field.staticOptions ?? (refKey ? ref[refKey] : []) ?? [];
      return (
        <ILMSSelect value={String(value)} disabled={disabled} onChange={(e) => setDefault(e.target.value || null)}>
          <option value="">(none)</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </ILMSSelect>
      );
    }
    if (field.type === 'checkbox') {
      return (
        <ILMSSelect value={value === '' ? '' : value ? 'yes' : 'no'} disabled={disabled} onChange={(e) => setDefault(e.target.value === '' ? null : e.target.value === 'yes')}>
          <option value="">(none)</option><option value="yes">Ticked</option><option value="no">Not ticked</option>
        </ILMSSelect>
      );
    }
    const numeric = field.type === 'number' || field.type === 'decimal';
    return (
      <ILMSInput type={numeric ? 'number' : 'text'} value={String(value)} disabled={disabled} placeholder="(none)"
        onChange={(e) => setDefault(e.target.value === '' ? null : numeric ? Number(e.target.value) : e.target.value)} />
    );
  };

  return (
    <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_0.5fr_1.2fr_1.5fr_1.2fr_auto] gap-3 py-3 items-start', isSet(rule) && 'bg-brand-green-50/40 -mx-2 px-2 rounded')}>
      <div className={cn(cell, 'sm:col-span-2 lg:col-span-1')}>
        <span className="text-body font-medium text-ink-900">{rule?.label || original}</span>
        <span className="text-helper-text text-ink-400">{rule?.label ? `was “${original}” · ` : ''}{english(field.section)}</span>
      </div>
      <div className={cell}>
        <span className={mobileLabel}>Must fill in?</span>
        {free ? (
          <ILMSSelect value={rule?.required ? 'yes' : 'no'} disabled={disabled || !!rule?.hidden} onChange={(e) => onChange({ required: e.target.value === 'yes' || null })}>
            <option value="no">Optional</option>
            <option value="yes">Required</option>
          </ILMSSelect>
        ) : <span className="text-secondary text-ink-500 pt-2">{LOCK_TEXT[lock]}</span>}
      </div>
      <div className={cell}>
        <span className={mobileLabel}>Hide</span>
        {free && !form.limited ? (
          <label className="flex h-11 items-center gap-2 text-secondary">
            <input type="checkbox" className="h-5 w-5" checked={!!rule?.hidden} disabled={disabled}
              onChange={(e) => onChange({ hidden: e.target.checked, required: e.target.checked ? null : rule?.required })} />
            <span className="lg:hidden">Hide this field</span>
          </label>
        ) : <span className="text-helper-text text-ink-300 pt-2">—</span>}
      </div>
      <div className={cell}>
        <span className={mobileLabel}>Name shown</span>
        {form.limited ? <span className="text-helper-text text-ink-300 pt-2">—</span> : (
          <ILMSInput value={rule?.label ?? ''} maxLength={80} disabled={disabled} placeholder={original} onChange={(e) => onChange({ label: e.target.value || null })} />
        )}
      </div>
      <div className={cell}>
        <span className={mobileLabel}>Help text</span>
        {form.limited ? <span className="text-helper-text text-ink-300 pt-2">—</span> : (
          <ILMSInput value={rule?.help ?? ''} maxLength={300} disabled={disabled} placeholder={field.helperText ? field.helperText.slice(0, 60) : '(none)'}
            title={field.helperText} onChange={(e) => onChange({ help: e.target.value || null })} />
        )}
      </div>
      <div className={cell}>
        <span className={mobileLabel}>Starting value</span>
        {defaultControl()}
        {rule?.hidden && (rule.default === undefined || rule.default === null) && <span className="text-helper-text text-ink-400">Hidden: saved empty.</span>}
        {rule?.hidden && rule.default !== undefined && rule.default !== null && <span className="text-helper-text text-ink-400">Hidden: always saved with this value.</span>}
      </div>
      <div className="flex items-start pt-2">
        {isSet(rule) && canEdit && (
          <button type="button" onClick={onReset} title="Back to how the form has it" className="text-ink-400 hover:text-critical-600">
            <RotateCcw className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
